/**
 * medList: pure assembly + deterministic time-of-day bucketing for the
 * Medication List builder (med_list tool).
 *
 * THREE input paths converge on ONE reviewed document shape (MedListDoc),
 * which is POSTed to the stateless med-list download endpoint (or saved as a
 * draft and downloaded by id) and rendered into the branded
 * MedicationList.docx:
 *
 *   - FROM THE RECORD (primary): assembleMedListFromRecord takes the ONE
 *     ClientRecordSnapshot the record page already holds (no new fetch), pulls
 *     the client's ACTIVE structured meds, and buckets them. NO model call:
 *     the meds are already structured, so a probabilistic tool never touches a
 *     real dose. Missing fields stay blank; nothing is invented.
 *
 *   - FROM A PASTE: the med_list generate handler parses pasted text into the
 *     SAME flat rows (MedListInputMedication); the FE then buckets them here
 *     with the SAME rules.
 *
 *   - FROM A PHOTO: the photo reader's medication rows are projected onto the
 *     same flat rows (photoMedToInput) and bucketed here too.
 *
 * So every path places "twice a day" identically, and every built list is
 * A to Z within each block (compareMedListRows, the hand mirror of the API's
 * api/_shared/aiTools/medListOrder.ts, which the downloads print by).
 *
 * bucketMedications is the deterministic bucketer. scheduleToBuckets is the
 * pure rule table (pinned by tests/ui/medList.test.ts): it maps a free-text
 * schedule to the five company-template sections. A med with NO recognizable
 * timing falls to "As Needed" with a review note, per the spec ("a med with
 * no clear timing -> a clearly-marked As Needed the CM fixes").
 *
 * THE ROWS: the helper edits one row per medicine (MedListItem), with the
 * record form's fields plus how it is taken (the printed Method column).
 * Every way in makes rows (itemFromInput, itemFromPhoto, itemFromRecord),
 * pre-ticked by scheduleToBuckets where the source has no ticks, and the
 * printed blocks are built from the rows by rowsToBuckets, the one place a
 * block is made. bucketMedications is that same path from flat inputs. A
 * draft saved as blocks before rows existed reopens through bucketsToRows.
 *
 * House rule: FE types are hand-mirrored from the backend zod
 * (api/_shared/aiTools/schemas/medListResponse.ts), NOT imported. The
 * MedListDoc keys are snake_case because the object is POSTed verbatim to the
 * zod-validated download endpoint (client_name / date / allergies / buckets).
 */

import type { AllergyRow, ClientRecordSnapshot, MedicationRow } from '@/lib/clientRecord';
import type { PhotoIntakeMedicationRow } from '@/lib/photoIntakeApi';
import { sortAllergiesBySeverity } from '@/lib/allergySort';
import { isCurrentMedication, upcomingMedicationChanges } from '@/lib/medicationTiming';
import { practiceTodayIso } from '@/lib/practiceDate';
import { proseDate } from '@/lib/proseDate';

// ---------------------------------------------------------------------
// The canonical shapes (hand-mirror of medListResponse.ts).
// ---------------------------------------------------------------------

export const MED_LIST_BUCKETS = ['morning', 'afternoon', 'evening', 'bedtime', 'as_needed'] as const;
export type MedListBucketKey = (typeof MED_LIST_BUCKETS)[number];

export const MED_LIST_BUCKET_LABELS: Record<MedListBucketKey, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
  bedtime: 'Bedtime',
  as_needed: 'As needed',
};

/** One rendered column row (the six template columns). All strings; a
 *  missing value is '' (a blank cell), never a fabricated value. */
export interface MedListRow {
  medication: string;
  /** The prescriber, printed beside the medication name (LP-32); '' when
   *  none, and on a block saved before the column existed. */
  prescriber: string;
  dose: string;
  method: string;
  purpose: string;
  notes: string;
}

export type MedListBuckets = Record<MedListBucketKey, MedListRow[]>;

/** The reviewed document, POSTed to the download endpoint and persisted on
 *  a draft (wire keys). */
export interface MedListDoc {
  client_name: string | null;
  /** The structured name the file name leads with ("Last, First"): set only
   *  from a linked client record, null when the name was typed (free text
   *  is never split). */
  client_last_name: string | null;
  client_first_name: string | null;
  /** The list date as a calendar day (YYYY-MM-DD). The tool pre-fills the
   *  practice's today; null is only the wire's "server fills today". */
  date: string | null;
  /** The NKDA-aware allergies line, or '' (server -> "[List allergies]"). */
  allergies: string;
  buckets: MedListBuckets;
  /** The rows the blocks are built from (the helper's editor). Absent on a
   *  draft saved before rows existed; withItems keeps the two in step. */
  items?: MedListItem[];
  /** The record's changes still to come (LP-41), printed under "Changes
   *  coming" after the blocks; absent or empty prints no section. Taken
   *  from the record with its current medications (the dialog, and Use the
   *  record's medications) and kept with those rows: a later replace by a
   *  read clears them, a reopened draft keeps what was saved. */
  changes_coming?: MedListChange[];
}

/** One change still to come: a medication that starts, or stops, on a
 *  later day (LP-41). Wire keys. */
export interface MedListChange {
  medication: string;
  dose: string;
  change: 'starts' | 'stops';
  /** The day, YYYY-MM-DD. */
  on: string;
}

/**
 * One medicine as the helper edits it: the record form's fields (name,
 * dose, When, directions, status, prescriber, reason) plus how it is
 * taken, the printed Method column. The prescriber prints in the
 * Prescribed by column (LP-32). Wire keys; the draft stores them as they
 * are.
 */
export interface MedListItem {
  name: string;
  dose: string;
  /** The ticked blocks, in the list's order. */
  times: MedListBucketKey[];
  directions: string;
  status: 'active' | 'discontinued';
  prescriber: string;
  reason: string;
  method: string;
}

/**
 * A flat medication row before bucketing — the shape the paste-path model
 * returns AND the shape record meds are projected into. `times`, when it
 * holds any block, places the row; otherwise `schedule` drives the
 * bucket. `notes` is any extra timing/handling detail.
 */
export interface MedListInputMedication {
  medication: string;
  dose: string;
  method: string;
  purpose: string;
  schedule: string;
  notes: string;
  /** The blocks ticked on the record; absent or empty when none are. */
  times?: readonly MedListBucketKey[];
}

/** The largest PDF the PDF door takes, in bytes: the server's
 *  MED_LIST_PDF_MAX_BYTES (api/_shared/aiTools/medListPdf.ts), mirrored so
 *  the browser refuses a larger one before sending it. */
export const MED_LIST_PDF_MAX_BYTES = 700 * 1024;

/** An empty five-bucket map. */
export function emptyBuckets(): MedListBuckets {
  return { morning: [], afternoon: [], evening: [], bedtime: [], as_needed: [] };
}

// ---------------------------------------------------------------------
// The order: A to Z within each block. Hand mirror of
// api/_shared/aiTools/medListOrder.ts (the downloads print by it), pinned
// pair by pair against it in tests/ui/medList.test.ts.
// ---------------------------------------------------------------------

// Case- and accent-blind, a digit run compared as a number ("Vitamin B6"
// before "Vitamin B12"); 'en-US' named explicitly so the browser and the
// Functions host collate alike.
const MED_LIST_COLLATOR = new Intl.Collator('en-US', { sensitivity: 'base', numeric: true });

/** By medication name, then dose; a full tie keeps arrival order (the sort is stable). */
export function compareMedListRows(
  a: { medication: string; dose: string },
  b: { medication: string; dose: string },
): number {
  return MED_LIST_COLLATOR.compare(a.medication, b.medication) || MED_LIST_COLLATOR.compare(a.dose, b.dose);
}

/** A new bucket map with every block in the medication order; the input is untouched. */
export function sortMedListBuckets(buckets: MedListBuckets): MedListBuckets {
  const out = emptyBuckets();
  for (const key of MED_LIST_BUCKETS) out[key] = [...buckets[key]].sort(compareMedListRows);
  return out;
}

// ---------------------------------------------------------------------
// scheduleToBuckets — the deterministic rule table.
// ---------------------------------------------------------------------

/**
 * Map a free-text schedule/frequency to the time-of-day buckets it belongs in.
 * Pure. Returns [] when NOTHING matches (the caller routes that med to As
 * Needed with a review note). Order of reasoning:
 *   1. explicit time-of-day words add their own bucket(s);
 *   2. a frequency count (bid/tid/qid/once) fills default buckets only when no
 *      explicit time already covered it;
 *   3. PRN / "as needed" adds As Needed (and stands alone when nothing else).
 */
export function scheduleToBuckets(scheduleRaw: string): MedListBucketKey[] {
  const s = ` ${scheduleRaw.toLowerCase().trim()} `;
  if (s.trim().length === 0) return [];

  const buckets = new Set<MedListBucketKey>();

  // 1. Explicit time-of-day words. Bedtime is checked with its own night
  // phrases so "at night" / "nightly" read as bedtime, while plain "evening" /
  // "dinner" / "pm" read as evening.
  const hasBedtime =
    /(bedtime|at night|before bed|nightly|overnight|at bed)/.test(s) || /\bq?hs\b/.test(s);
  const hasMorning =
    /(morning|breakfast|sunrise|before breakfast|with breakfast|upon waking|wake|qam|q\.?a\.?m)/.test(
      s,
    ) || /\b\d{1,2}\s?am\b/.test(s);
  const hasAfternoon = /(afternoon|noon|midday|mid-day|lunch|with lunch|midafternoon)/.test(s);
  const hasEvening =
    /(evening|dinner|supper|with dinner|teatime|qpm|q\.?p\.?m)/.test(s) || /\b\d{1,2}\s?pm\b/.test(s);

  if (hasMorning) buckets.add('morning');
  if (hasAfternoon) buckets.add('afternoon');
  if (hasEvening) buckets.add('evening');
  if (hasBedtime) buckets.add('bedtime');

  const prn = /(as needed|as required|when needed|as necessary|\bprn\b|p\.r\.n)/.test(s);

  // 2. Frequency counts. Fill defaults ONLY when no explicit time already set
  // a bucket, so "twice a day at breakfast and dinner" keeps its two named
  // times rather than doubling up.
  const explicitTimeSet = buckets.size > 0;
  const isBid = /(twice a day|twice daily|two times a day|\bbid\b|b\.i\.d|\b2x\b)/.test(s);
  const isTid = /(three times a day|three times daily|\btid\b|t\.i\.d|\b3x\b)/.test(s);
  const isQid = /(four times a day|four times daily|\bqid\b|q\.i\.d|\b4x\b)/.test(s);
  const isDaily =
    /(once a day|once daily|one time a day|every day|daily|\bqd\b|q\.d|\bqday\b|every morning|each morning|\bq24h?\b)/.test(
      s,
    ) || /\b1x\b/.test(s);

  if (!explicitTimeSet) {
    if (isQid) {
      buckets.add('morning');
      buckets.add('afternoon');
      buckets.add('evening');
      buckets.add('bedtime');
    } else if (isTid) {
      buckets.add('morning');
      buckets.add('afternoon');
      buckets.add('evening');
    } else if (isBid) {
      buckets.add('morning');
      buckets.add('evening');
    } else if (isDaily) {
      // A single daily dose with no stated time defaults to the morning.
      buckets.add('morning');
    }
  }

  // 3. PRN. Adds As Needed; stands alone when nothing else matched.
  if (prn) buckets.add('as_needed');

  // Preserve document order.
  return MED_LIST_BUCKETS.filter((k) => buckets.has(k));
}

/** The review note appended to a med whose timing could not be recognized. */
export const UNBUCKETED_NOTE = 'Timing not specified, please confirm';

/** A blank row, for a medicine added by hand. */
export function emptyMedListItem(): MedListItem {
  return { name: '', dose: '', times: [], directions: '', status: 'active', prescriber: '', reason: '', method: '' };
}

/** The ticks in the list's order, each once. */
function canonicalTimes(times: readonly string[]): MedListBucketKey[] {
  return MED_LIST_BUCKETS.filter((k) => times.includes(k));
}

/**
 * The printed blocks, built from the rows: the one place a block is made.
 * An active row goes in every block it has ticked (a medicine taken twice
 * is in both); an unticked row goes in As needed with the review sentence;
 * a discontinued row and a nameless row are left off. The six columns are
 * the row's name, prescriber, dose, how it is taken, reason and
 * directions. Each block is A to Z.
 */
export function rowsToBuckets(items: readonly MedListItem[]): MedListBuckets {
  const out = emptyBuckets();
  for (const item of items) {
    const name = item.name.trim();
    if (name.length === 0 || item.status === 'discontinued') continue;
    const ticked = canonicalTimes(item.times);
    const unticked = ticked.length === 0;
    const keys: MedListBucketKey[] = unticked ? ['as_needed'] : ticked;
    const notes = dedupeJoin(unticked ? [item.directions, UNBUCKETED_NOTE] : [item.directions]);
    for (const key of keys) {
      out[key].push({
        medication: name,
        prescriber: item.prescriber.trim(),
        dose: item.dose.trim(),
        method: item.method.trim(),
        purpose: item.reason.trim(),
        notes,
      });
    }
  }
  return sortMedListBuckets(out);
}

/**
 * The rows an old draft's blocks came from: a row identical in every
 * column, found in several blocks, is one medicine ticked in those blocks;
 * the review sentence is taken off its directions and a row that carried
 * it comes back unticked. Status was never stored on a block, so it comes
 * back active; the prescriber comes back as the block printed it, blank
 * on a block saved before the column existed (LP-32).
 */
export function bucketsToRows(buckets: MedListBuckets): MedListItem[] {
  const rows: MedListItem[] = [];
  const byKey = new Map<string, MedListItem>();
  for (const key of MED_LIST_BUCKETS) {
    for (const r of buckets[key]) {
      const prescriber = (r.prescriber ?? '').trim();
      const id = JSON.stringify([r.medication, prescriber, r.dose, r.method, r.purpose, r.notes]);
      const flagged = r.notes.includes(UNBUCKETED_NOTE);
      let row = byKey.get(id);
      if (row === undefined) {
        // The review sentence is the last part when it is there (it holds
        // a comma itself, so it is taken off whole, never split).
        const directions = flagged
          ? r.notes.slice(0, r.notes.lastIndexOf(UNBUCKETED_NOTE)).replace(/,\s*$/, '').trim()
          : r.notes;
        row = { ...emptyMedListItem(), name: r.medication, prescriber, dose: r.dose, method: r.method, reason: r.purpose, directions };
        byKey.set(id, row);
        rows.push(row);
      }
      if (!flagged) row.times = canonicalTimes([...row.times, key]);
    }
  }
  return rows;
}

/** A pasted (or record-projected) flat medicine as a row: its own ticks,
 *  else ticks read from its timing words by scheduleToBuckets; its notes
 *  then its timing words as its directions. */
export function itemFromInput(med: MedListInputMedication): MedListItem {
  const ticked = canonicalTimes(med.times ?? []);
  return {
    ...emptyMedListItem(),
    name: med.medication.trim(),
    dose: med.dose.trim(),
    method: med.method.trim(),
    reason: med.purpose.trim(),
    directions: dedupeJoin([med.notes, med.schedule]),
    times: ticked.length > 0 ? ticked : scheduleToBuckets(med.schedule),
  };
}

/** A photo-read medicine as a row; unlike the flat input, it keeps the
 *  prescriber the photo carried. */
export function itemFromPhoto(row: PhotoIntakeMedicationRow): MedListItem {
  return { ...itemFromInput(photoMedToInput(row)), prescriber: (row.prescriber ?? '').trim() };
}

/** A record medicine as a row: its own ticks (else its directions read
 *  as the record list reads them), its reason and its prescriber. */
export function itemFromRecord(m: MedicationRow): MedListItem {
  return { ...itemFromInput(recordMedToInput(m)), prescriber: (m.prescriber ?? '').trim() };
}

/** The document with these rows and the blocks made from them. */
export function withItems(doc: MedListDoc, items: MedListItem[]): MedListDoc {
  return { ...doc, items, buckets: rowsToBuckets(items) };
}

/** A document's rows: its own, else rebuilt from its blocks. */
export function itemsOf(doc: MedListDoc): MedListItem[] {
  return doc.items ?? bucketsToRows(doc.buckets);
}

/**
 * Bucket a flat list of meds into the five sections: the rows path
 * (itemFromInput, then rowsToBuckets), so every way in places a medicine
 * by the same rule. A med with ticked blocks (`times`) goes in exactly
 * those; otherwise its schedule text places it, and a med with no
 * recognizable schedule lands in As Needed with the review note.
 */
export function bucketMedications(meds: MedListInputMedication[]): MedListBuckets {
  return rowsToBuckets(meds.map(itemFromInput));
}

function dedupeJoin(parts: string[]): string {
  const seen = new Set<string>();
  const kept: string[] = [];
  for (const p of parts) {
    const v = p.trim();
    if (v.length === 0) continue;
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(v);
  }
  return kept.join(', ');
}

// ---------------------------------------------------------------------
// From the client record — pure assembly (no fetch, no model).
// ---------------------------------------------------------------------

/**
 * The meds the list's blocks print: the face sheet's rule, the ones the
 * client takes on `today` (the practice day; LP-41). One starting later
 * prints under Changes coming instead; archived, discontinued and stopped
 * history stays on the record.
 */
export function activeMedicationsForList(meds: MedicationRow[], today: string): MedicationRow[] {
  return meds.filter((m) => isCurrentMedication(m, today));
}

/** A change as the list prints it: 'Starts November 20, 2026'. The
 *  server prints the same words (medListChangeWords,
 *  api/_shared/aiTools/medListWords.ts), pinned pair by pair in
 *  tests/ui/medicationTiming.test.ts. */
export function medListChangeWords(c: Pick<MedListChange, 'change' | 'on'>): string {
  return `${c.change === 'starts' ? 'Starts' : 'Stops'} ${proseDate(c.on)}`;
}

/** The record's changes still to come for the list's Changes coming
 *  section, soonest first (upcomingMedicationChanges). */
export function medListChangesFromRecord(record: ClientRecordSnapshot, today: string): MedListChange[] {
  return upcomingMedicationChanges(record.medications, today).map((c) => ({
    medication: c.row.name,
    dose: c.row.dose ?? '',
    change: c.change,
    on: c.on,
  }));
}

/**
 * Project a record MedicationRow onto the flat med-list input. The record has
 * no `method` column (route is not captured), so Method stays blank; `reason`
 * becomes Purpose. The ticks place it; the directions (`schedule`) only when
 * none is ticked, and they ride as its timing detail. Nothing is invented — an
 * absent field is ''. The flat input carries no prescriber; itemFromRecord
 * adds it to the row, and the row prints it (LP-32).
 */
export function recordMedToInput(m: MedicationRow): MedListInputMedication {
  return {
    medication: m.name,
    dose: m.dose ?? '',
    method: '',
    purpose: m.reason ?? '',
    schedule: m.schedule ?? '',
    notes: '',
    times: m.timesOfDay ?? [],
  };
}

/**
 * Project a photo-read medication row onto the flat med-list input. The
 * photo carries no method, purpose, or notes, so those stay blank; an
 * absent dose or schedule is ''. The flat input carries no prescriber;
 * itemFromPhoto adds it to the row, and the row prints it (LP-32).
 */
export function photoMedToInput(row: PhotoIntakeMedicationRow): MedListInputMedication {
  return {
    medication: row.name,
    dose: row.dose ?? '',
    method: '',
    purpose: '',
    schedule: row.schedule ?? '',
    notes: '',
    // The photo's own When, when it read one; else the schedule places it.
    ...(row.times_of_day !== null && row.times_of_day !== undefined && row.times_of_day.length > 0
      ? { times: row.times_of_day.filter((t): t is MedListBucketKey => (MED_LIST_BUCKETS as readonly string[]).includes(t)) }
      : {}),
  };
}

/** The record's structured name for the list, trimmed; a blank part is null. */
function structuredNameOf(
  record: ClientRecordSnapshot,
): Pick<MedListDoc, 'client_last_name' | 'client_first_name'> {
  const part = (v: string | null): string | null => {
    const t = (v ?? '').trim();
    return t.length > 0 ? t : null;
  };
  return {
    client_last_name: part(record.vitals.lastName),
    client_first_name: part(record.vitals.firstName),
  };
}

// This document states a severity of 'unknown' outright ("severity
// unknown") rather than staying silent about it — unlike the strip's
// inline marker (allergySeverityMarker, src/lib/allergySort.ts), which
// hides an unstated/unknown severity. A printed list a family carries to
// an appointment shouldn't imply "nothing to report" when the honest
// answer is "asked, and nobody knows" — so this map keeps its own word
// for 'unknown' rather than sharing the strip's.
const SEVERITY_WORD: Record<NonNullable<AllergyRow['severity']>, string> = {
  severe: 'severe',
  moderate: 'moderate',
  mild: 'mild',
  unknown: 'severity unknown',
};

/**
 * The allergies line for the list, honoring the record's three honest states
 * (the AllergiesCard / FaceSheet rule):
 *   - recorded rows  -> "Sulfa (severe); Penicillin (rash, moderate)"
 *   - confirmed none -> "No known drug allergies (NKDA)"
 *   - not recorded   -> "Not recorded"  (never blank -> never "[List allergies]"
 *                       on the record path; the CM can still edit it)
 * An empty allergy list NEVER means "allergy-free". Most severe first,
 * then alphabetical — the ONE order every allergy list on the record
 * shares (src/lib/allergySort.ts).
 */
export function medListAllergiesLine(record: ClientRecordSnapshot): string {
  const rows = sortAllergiesBySeverity(record.allergies.filter((a) => a.isActive));
  if (rows.length > 0) {
    return rows
      .map((a) => {
        const detail = [a.reaction, a.severity ? SEVERITY_WORD[a.severity] : null]
          .filter(Boolean)
          .join(', ');
        return detail ? `${a.substance} (${detail})` : a.substance;
      })
      .join('; ');
  }
  if (record.allergiesNoneKnownOn !== null) return 'No known drug allergies (NKDA)';
  return 'Not recorded';
}

/**
 * Assemble the reviewed med-list document from the record snapshot — the pure
 * primary path. No fetch, no model: the meds are already structured. `today`
 * (the practice day) pre-fills the date; absent, the server fills today.
 * Which medications are current, and which changes are still to come, is
 * decided on the practice's today either way (LP-41).
 */
export function assembleMedListFromRecord(record: ClientRecordSnapshot, today?: string): MedListDoc {
  const day = today ?? practiceTodayIso();
  const items = activeMedicationsForList(record.medications, day).map(itemFromRecord);
  return {
    client_name: record.vitals.displayName || null,
    ...structuredNameOf(record),
    date: today ?? null,
    allergies: medListAllergiesLine(record),
    buckets: rowsToBuckets(items),
    items,
    changes_coming: medListChangesFromRecord(record, day),
  };
}

/** The blank list the tool starts with: today's date, nothing else. */
export function emptyMedListDoc(today: string): MedListDoc {
  return {
    client_name: null,
    client_last_name: null,
    client_first_name: null,
    date: today,
    allergies: '',
    buckets: emptyBuckets(),
    items: [],
  };
}

/**
 * A linked client's record is the source for the header: the display name,
 * the structured name, and the NKDA-aware allergies line replace whatever
 * was typed; the meds, their changes still to come and the date are the
 * CM's and stay (LP-41: the changes travel with the rows they were taken
 * with, so a list reopened later never pairs old rows with new changes).
 * Pure; returns a new document.
 */
export function applyLinkedClient(doc: MedListDoc, record: ClientRecordSnapshot): MedListDoc {
  return {
    ...doc,
    client_name: record.vitals.displayName || null,
    ...structuredNameOf(record),
    allergies: medListAllergiesLine(record),
  };
}

/**
 * The list date is a recorded date: a real calendar day in YYYY-MM-DD that
 * is not after `today`. Mirrors the server's coerceMedListDate; the form
 * uses it to block Save / Download and show the copy before a round trip.
 */
export function isMedListDateValid(date: string | null, today: string): boolean {
  if (date === null || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const [y, m, d] = date.split('-').map((n) => Number.parseInt(n, 10));
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m! - 1 || dt.getUTCDate() !== d) return false;
  return date <= today;
}

/** Total med rows across all buckets (drives "no active meds" empty states). */
export function totalMedCount(buckets: MedListBuckets): number {
  return MED_LIST_BUCKETS.reduce((n, k) => n + buckets[k].length, 0);
}
